import { ToArray } from '../../modules/ToArray.js';
import { FlagManager } from './FlagManager.js';
import { config } from '../../app-configuration/config.js';

import firebase from 'firebase/compat/app';
import { Path } from '../../modules/Path.js';
import { GetTriggeredActions, OverwriteAction } from '../../modules/ActionTracking.js';
import { LikeArray } from '../../modules/LikeArray.js';
import { BlockLargeUpdates } from '../../modules/BlockLargeUpdates.js';
import { getEmptyProgressGroup } from '../../workers/mobile/FirebaseWebWorker/MobileUpdateManager.js';

/** @typedef {import('../../modules/DatabaseUpdateParser.js').DatabaseUpdateParser} DatabaseUpdateParser */

// Only set up FirebaseWorker once
if (!self.FirebaseWorker) {
  self.FirebaseWorker = {
    /* Private Variables */
    fm: new FlagManager(),
    promises: {},
    _promises: {},
    _callbacks: {},
    _eventCallbacks: {},
    online: false,
    /** @type {Record<string,DatabaseUpdateParser>} */
    updateParsers: {},
    _offlineJobs: [],
    _offlineJobsListeners: {},
    _updateGroupProgress: {},
    _updateProgressListeners: {},
    /* Public Methods */

    getOfflineJobs: async function () {
      const id = firebase.database().ref().push().key;
      const promise = this._promise(id);
      this._postMessage({ type: 'getOfflineJobs', id });
      return promise;
    },

    listenToOfflineJobs: function (callback) {
      const listenerId = firebase.database().ref().push().key;
      this._offlineJobsListeners[listenerId] = { callback };

      // Call the callback with the initial data
      callback(this._offlineJobs);

      const disconnect = () => {
        delete this._offlineJobsListeners[listenerId];
      };
      return disconnect;
    },

    listenToUpdateProgressForJob: function (jobId, callback) {
      const progressGroupId = jobId;

      const listenerId = firebase.database().ref().push().key;
      this._updateProgressListeners[listenerId] = { progressGroupId, callback };

      const initialProgress = this._updateGroupProgress[progressGroupId] ?? getEmptyProgressGroup();
      if (initialProgress) callback(initialProgress);

      const disconnect = () => {
        delete this._updateProgressListeners[listenerId];
      };
      return disconnect;
    },
    getUnsavedChangesForJob: async function (jobId) {
      const id = firebase.database().ref().push().key;
      const promise = this._promise(id);
      this._postMessage({ type: 'getUnsavedChangesForJob', jobId, id });
      return promise;
    },
    clearUnsavedChangesForJob: async function (jobId) {
      const id = firebase.database().ref().push().key;
      const promise = this._promise(id);
      this._postMessage({ type: 'clearUnsavedChangesForJob', jobId, id });
      return promise;
    },
    app: function (app, _record) {
      let _newRecord = Path.copy(_record) || {};
      _newRecord.app = app || '[DEFAULT]';
      return {
        auth: firebase.auth,
        name: _newRecord.app,
        database: (databaseURL) => this.database(databaseURL, _newRecord),
        ref: (path) => this.ref(path, _newRecord)
      };
    },
    auth: function () {
      return firebase.auth();
    },
    blockWrites: function (value) {
      this._postMessage({ type: 'blockWrites', value });
    },
    database: function (databaseURL, _record) {
      let _newRecord = Path.copy(_record) || {};
      _newRecord.databaseURL = databaseURL;
      return {
        ref: (path) => this.ref(path, _newRecord)
      };
    },
    // Pass-through of firestore, no offline support
    // TODO (07-02-2022): This is a getter to avoid a timing issue on deployed code
    get firestore() {
      return firebase.firestore;
    },
    get storage() {
      return firebase.storage;
    },
    ref: function (path, _record) {
      let _newRecord = Path.copy(_record) || {};
      // Sanitize path.
      path = Path.clean(path, '/');
      if (path == null || path === '') path = undefined;
      _newRecord.path = path;
      return this._ref(_newRecord);
    },
    onChange: function () {
      return this.fm.onChange(...arguments);
    },
    offChange: function () {
      return this.fm.offChange(...arguments);
    },
    registerActionTrackingForJob: function (models, companyId, jobId, { isNotActiveJob } = {}) {
      // Convert models to an array and insert wildcard paths into models.
      models = ToArray(models).map((action) => {
        if (action.type == 'job') {
          action.path = `photoheight/jobs/*/metadata/${action.attribute}`;
        } else if (action.type == 'job_modified') {
          action.path = `photoheight/jobs/*`;
        } else {
          action.path = `photoheight/jobs/*/nodes/*/attributes/${action.attribute}`;
        }
        return action;
      });
      // notify worker if we are using mobile
      if (self.isMobile) this._postMessage({ type: 'registerActionTrackingForJob', models, companyId, jobId });
      // Skip updating the global active-job state and notifying listeners when registering a non-active (cached) job
      if (isNotActiveJob) return;
      this.actionTrackingModels = models;
      this.actionTrackingCompanyId = companyId;
      this.fm.set('actionTrackingModelsUpdated', Date.now());
    },

    /**
     * Attaches an update parser to the worker (after first removing a parser of the same name).
     * @param {string} parserName - The name of the parser to attach. If a parser with this name already exists, it will be overwritten.
     * @param {DatabaseUpdateParser} updateParser - The parser to attach.
     */
    attachUpdateParser: function (parserName, updateParser) {
      this.detachUpdateParser(parserName);
      this.updateParsers[parserName] = updateParser;
    },
    /**
     * Detaches (removes) an update parser from the worker.
     * @param {string} parserName - The name of the parser to detach.
     */
    detachUpdateParser: function (parserName) {
      delete this.updateParsers[parserName];
    },
    /**
     * Runs all update parsers on the given update.
     * @param {string} path - The path of the update.
     * @param {object} update - The update object.
     * @returns {Promise<void>} A promise that resolves when all update parsers have finished.
     */
    runUpdateParsers: async function (path, update) {
      /** @type {DatabaseUpdateParser[]} */
      const parsersArray = Object.values(this.updateParsers ?? {});
      const results = await Promise.allSettled(
        parsersArray.map(async (parser) => {
          const [, handlerRes] = await parser.parseUpdate(path, update);
          return handlerRes;
        })
      );

      // Throw an error if any of the parsers failed
      const errors = results.filter((result) => result.status === 'rejected');
      if (errors.length) {
        throw new Error(
          `Error(s) running update parsers:\n\n${
            /** @ts-ignore: TS thinks `reason` might be undefined, it can't infer that we're only working with errors */
            errors.map(({ reason }) => reason?.stack).join('\n\n')
          }`
        );
      }
    },

    /**
     * Posts a message to FirebaseWebWorker to handle when the active company changes
     * @param {string} userGroup - The new active company id
     */
    activeCompanyChanged: function (userGroup) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // make sure we have all the data we need
        if (userGroup) {
          let id = firebase.database().ref().push().key;
          let promise = this._promise(id);
          this._postMessage({ type: 'activeCompanyChanged', userGroup, id });
          return promise;
        } else console.warn('activeCompanyChanged: Needs userGroup');
      } else console.warn('activeCompanyChanged: Not using mobile');
    },
    /**
     * Posts a message to FirebaseWebWorker to refresh the list of offline jobs.
     */
    refreshOfflineJobs: function () {
      this._postMessage({ type: 'refreshOfflineJobs' });
    },
    /**
     * Posts a message to FirebaseWebWorker to refresh unsaved changes progress.
     * This is used when we regain focus on mobile to ensure that progress tracking data is up to date.
     * This is mainly a workaround for multi-tab use to ensure we capture the progress of any changes made in another tab.
     */
    refreshUnsavedChangesProgress: function () {
      this._postMessage({ type: 'refreshUnsavedChangesProgress' });
    },
    // TODO (1/15/2025) - This does not have to be async
    /**
     * Posts a message to FirebaseWebWorker to setup a job for offline use
     * @param {string} jobId - The ID of the job to setup for offline use.
     */
    setupOfflineJob: async function (jobId) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // make sure we have all the data we need
        if (jobId) {
          // if we are online then setup the offline job
          if (this.online) {
            // tell FirebaseWebWorker to setup this job for offline use
            let id = firebase.database().ref().push().key;
            let promise = this._promise(id);
            this._postMessage({ type: 'setupOfflineJob', jobId, id });
            return promise;
          } else console.warn('setupOfflineJob: Offline setup is not supported when offline');
        } else console.warn('setupOfflineJob: Needs jobId and userGroup');
      } else console.warn('setupOfflineJob: Not using mobile');
    },
    /**
     * Posts a message to re-cache an offline job
     * @param {string} jobId - The ID of the job to refresh data for.
     */
    recacheOfflineJob: async function (jobId) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // make sure we have all the data we need
        if (jobId) {
          if (this.online) {
            let id = firebase.database().ref().push().key;
            let promise = this._promise(id);
            this._postMessage({ type: 'recacheOfflineJob', jobId, id });
            return promise;
          } else console.warn('setupOfflineJob: Offline setup is not supported when offline');
        } else console.warn('setupOfflineJob: Needs jobId and userGroup');
      } else console.warn('setupOfflineJob: Not using mobile');
    },
    /**
     * Posts a message to the FirebaseWebWorker to clear the offline path directory's data
     * @param {boolean} turnOffListeners - Whether to turn off listeners when clearing the offline path directory's data.
     */
    clearOfflinePathDirectory: function (turnOffListeners) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // tell FirebaseWebWorker to clear the offline path directory's data
        let id = firebase.database().ref().push().key;
        let promise = this._promise(id);
        this._postMessage({ type: 'clearOfflinePathDirectory', turnOffListeners, id });
        return promise;
      } else console.warn('clearOfflinePathDirectory: Not using mobile');
    },
    /**
     * Post a message to the FirebaseWebWorker to clear data for an offline job from the IDB and turn off listeners if specified
     * @param {string} jobId - The job to clear offline data for.
     * @param {object} [options] - An object containing options for clearing the offline job.
     */
    clearOfflineJob: function (jobId, options = {}) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        if (jobId) {
          // tell FirebaseWebWorker to clear this job's offline data
          let id = firebase.database().ref().push().key;
          let promise = this._promise(id);
          this._postMessage({ type: 'clearOfflineJob', jobId, options, id });
          return promise;
        } else console.warn('clearOfflineJob: Needs jobId');
      } else console.warn('clearOfflineJob: Not using mobile');
    },
    clearAllOfflineJobs: function () {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // tell FirebaseWebWorker to clear all offline jobs
        let id = firebase.database().ref().push().key;
        let promise = this._promise(id);
        this._postMessage({ type: 'clearAllOfflineJobs', id });
        return promise;
      } else console.warn('clearAllOfflineJobs: Not using mobile');
    },
    /**
     * Post a message to the FirebaseWebWorker when the job id changes
     * @param {string} jobId - The id of the job that is currently selected.
     * @param {string} userGroup - The current user's active company
     */
    setCurrentJobId: function (jobId, userGroup) {
      // make sure we are using mobile
      if (location.pathname.includes('mobile')) {
        // make sure we have all the data we need
        if (jobId != null) {
          // tell FirebaseWebWorker to set the current jobId
          let id = firebase.database().ref().push().key;
          let promise = this._promise(id);
          this._postMessage({ type: 'setCurrentJobId', jobId, userGroup, id });
          return promise;
        } else console.warn('setCurrentJobId: Needs jobId');
      } else console.warn('setCurrentJobId: Not using mobile');
    },
    /* Private Methods */
    _notifyOfflineJobListeners: function () {
      for (const listenerId in this._offlineJobsListeners) {
        const listener = this._offlineJobsListeners[listenerId];
        listener.callback(this._offlineJobs);
      }
    },
    _notifyUpdateProgressListeners: function (progressGroupId) {
      const updateGroupProgress = this._updateGroupProgress[progressGroupId];
      for (const listenerId in this._updateProgressListeners) {
        const listener = this._updateProgressListeners[listenerId];
        if (listener.progressGroupId !== progressGroupId) continue;
        listener.callback(updateGroupProgress);
      }
    },
    _actionTracking: async function actionTracking(rootPath, update, options) {
      options = options || {};
      // Use the configured tracking models or use one passed in from options
      let actionTrackingModel =
        this.actionTrackingModels ||
        ToArray(options.actionTrackingModels).map((action) => {
          if (action.type == 'job') action.path = `photoheight/jobs/*/metadata/${action.attribute}`;
          else if (action.type == 'job_modified') action.path = `photoheight/jobs/*`;
          else action.path = `photoheight/jobs/*/nodes/*/attributes/${action.attribute}`;
          return action;
        });
      let actions = GetTriggeredActions(rootPath, update, actionTrackingModel);
      if (Array.isArray(actions) && actions.length) await Promise.all(actions.map((action) => this._recordAction(action, options)));
    },

    /**
     * Post a message to the FirebaseWebWorker to sync the IndexedDB version with the local version
     */
    _syncIDBVersion: function () {
      this._postMessage({ type: 'syncIDBVersion' });
    },
    _recordAction: async function (action, options) {
      options = options || {};
      // Use the configured company id or use one passed in from options
      let actionTrackingCompanyId = this.actionTrackingCompanyId || options.actionTrackingCompanyId;
      const actionTrackingRef = firebase.firestore().collection(`companies/${actionTrackingCompanyId}/action_tracking`);
      // Filter the ref to use for searching for existing records
      let filteredRef = actionTrackingRef.where('job_id', '==', action.jobId).where('action_id', '==', action.$key);
      if (action.type != 'job' && action.type != 'job_modified') filteredRef = filteredRef.where('node_id', '==', action.nodeId);
      const s = await filteredRef.get();
      // Create the new action
      let newAction = {
        uid: katapultAuth.user.uid,
        user_group: katapultAuth.userGroup,
        root_company: katapultAuth.rootCompany,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        job_id: action.jobId,
        ...(action.nodeId && { node_id: action.nodeId }),
        ...(action.type && { type: action.type }),
        action_id: action.$key,
        action_name: action.action_name,
        value: action.value,
        order: action.order != null ? action.order : null
      };
      // Remove value data for job modified actions
      if (action.type == 'job_modified') delete newAction.value;
      let suppressEvents = action.type == 'job_modified' || options.suppressEvents;
      // If the options object has any overwrites, do those
      if (options.overwrites) Object.assign(newAction, options.overwrites);
      // If the ref is empty (there are no existing records) or we allow multiple of this action, add a new record
      if (s.empty || Path.get(action, 'behaviors.allow_multiple')) {
        // If the value of the action should be the delta from the last action, update value
        if (Path.get(action, 'behaviors.value_is_delta'))
          newAction.value = newAction.value - (s.empty ? 0 : s.docs.map((doc) => doc.data()).reduce((acc, doc) => acc + doc.value, 0));
        // Put the new action in firestore
        await actionTrackingRef.add(newAction);
        // Provided we're not suppressing events, fire a success events
        if (!suppressEvents) {
          const successEvent = new CustomEvent('action-tracking-success', {
            bubbles: true,
            detail: { action: newAction }
          });
          window.dispatchEvent(successEvent);
        }
      }
      // Update the time on the existing action
      else if (action.type == 'job_modified') {
        firebase.firestore().doc(`companies/${actionTrackingCompanyId}/action_tracking/${s.docs[0].id}`).update({
          timestamp: firebase.firestore.FieldValue.serverTimestamp()
        });
      }
      // If we allow overwrites, overwrite the last action
      else if (Path.get(action, 'behaviors.overwrite_without_prompt')) {
        const lastAction = s.docs
          .map((doc) => ({ ...doc.data(), $key: doc.id }))
          .sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())
          .pop();

        await OverwriteAction(lastAction, newAction, actionTrackingCompanyId, firebase.firestore());

        // Provided we're not suppressing events, fire a success events
        if (!suppressEvents) {
          const successEvent = new CustomEvent('action-tracking-success', {
            bubbles: true,
            detail: { action: newAction }
          });
          window.dispatchEvent(successEvent);
        }
      }
      // Otherwise, (provided we're not suppressing events) dispatch a collision event
      else if (!suppressEvents) {
        const lastAction = s.docs
          .map((doc) => ({ ...doc.data(), $key: doc.id }))
          .sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())
          .pop();
        const collisionEvent = new CustomEvent('action-tracking-collision', {
          bubbles: true,
          detail: {
            newAction: newAction,
            oldAction: lastAction,
            itemType: lastAction?.type || 'node',
            behaviors: action.behaviors
          }
        });
        window.dispatchEvent(collisionEvent);
      }
    },
    /**
     * Set up the FirebaseWebWorker, turn on a listener for online/offline status, initialize the worker, and sync IDB versions
     */
    _init: function () {
      // init the server value timestamp parameter for FirebaseWorker
      self.FirebaseWorker.database.ServerValue = { TIMESTAMP: { '.sv': 'timestamp' } };
      // only init FirebaseWorker if we are in mobile
      if (self.isMobile) {
        if (!this.initialized) {
          this.dbName = config.firebaseConfigs[config.appName].databaseURL.replace(/(https:\/\/)|(\.firebaseio\.com)/g, '');
          // Setup worker.
          this._worker = new Worker('/source/_resources/workers/mobile/FirebaseWebWorker.js', { type: 'module' });

          // Setup the listener that will keep track of whether we are online or offline
          this._setupOnlineListener();

          this._worker.addEventListener('message', (e) => {
            let message = e.data;
            if (message.type === 'promise') {
              const promise = this._promises[message.id];
              if (!promise) return;
              if (message.returnType == 'res') {
                promise.resolve(message.res);
              } else {
                if (message.error) promise.reject(message.error);
                else promise.resolve({ val: () => message.data });
              }
            } else if (message.type === 'oncePromise') {
              this._promises[message.id].resolve(this._snapshot({ data: message.data, record: message.record }));
            }
            // TODO (1/15/2025): Unused?
            else if (message.type === 'callback') {
              let callback = (Path.get(this._promises, `${message.id}.record.callbacks`) || {})[message.callbackType];
              if (callback) callback(message.res);
            } else if (message.type === 'processUpdate') {
              this._processUpdate(message.refKey, message.update);
            } else if (message.type === 'device_id_changed') {
              this.fm.set('deviceId', message.deviceId);
            } else if (message.type === 'syncing_changed') {
              this.fm.set('syncing', message.syncing);
            } else if (message.type === 'synced_changed') {
              this.fm.set('synced', message.synced);
            } else if (message.type === 'unsaved_changes') {
              this.fm.set('unsavedChanges', message.unsavedChanges);
            } else if (message.type === 'sync_error_changed') {
              this.fm.set('syncError', message.err);
            } else if (message.type == 'indexed_db_connection_error') {
              this.fm.set('indexedDBConnectionError', message.indexedDBConnectionError);
            } else if (message.type === 'action_tracking_collision') {
              const collisionEvent = new CustomEvent('action-tracking-collision', {
                bubbles: true,
                detail: message.actionTrackingCollision
              });
              window.dispatchEvent(collisionEvent);
            } else if (message.type === 'offline_jobs_changed') {
              this._offlineJobs = message.offlineJobs;
              this._notifyOfflineJobListeners();
            } else if (message.type == 'update_group_progress') {
              this._updateGroupProgress[message.progress.progressGroupId] = message.progress;
              this._notifyUpdateProgressListeners(message.progress.progressGroupId);
            }
          });

          // initialize FirebaseWebWorker
          this._postMessage({
            type: 'init',
            dbName: this.dbName,
            firebaseConfig: config.firebaseConfigs[config.appName],
            version: `${config.version}+${config.buildNumber}`
          });
          if (navigator.storage) {
            navigator.storage.persist();
          }

          // Setup flag promises.
          [
            {
              flag: 'unsavedChanges',
              negate: true
            },
            {
              flag: 'synced'
            }
          ].forEach((config) => {
            this.onChange(config.flag, (value) => {
              let promise = this._promises[config.flag];
              if (config.negate) value = !value;
              if (value) {
                if (promise != null) {
                  // Resolve the promise.
                  promise.resolve();
                }
              } else {
                if (promise == null) {
                  // Setup the promise.
                  this._promise(config.flag);
                }
              }
            });
          });

          // Setup listeners to keep FirebaseIDB in sync with IndexedDB
          window.addEventListener('focus', () => {
            // this occurs when we click on a tab
            // Upon entering the tab check to see if we need to sync FirebaseIDB
            if (sessionStorage.getItem('syncIDBVersion')) {
              this._syncIDBVersion();
              sessionStorage.removeItem('syncIDBVersion');
            }
          });
          window.addEventListener('blur', () => {
            // this occurs when we click away from a tab
            // Upon exiting the tab set a flag to sync FirebaseIDB
            sessionStorage.setItem('syncIDBVersion', 'true');
          });

          this.initialized = true;
        }
      }
    },
    /**
     * Set up a listener to track online/offline status through firebase and post a message to
     * FirebaseWebWorker when the online status changes.
     */
    _setupOnlineListener: function () {
      // Note: we do this in the client and not in FirebaseWebWorker.js because for some reason the listener is MUCH slower at detecting connection changes
      // when the listener is inside a worker.
      firebase
        .database()
        .ref('.info/connected')
        .on('value', (s) => {
          this.online = s.val();
          this._postMessage({ type: 'online_changed', online: this.online });
        });
    },
    /**
     * Notifies registered callbacks of changes for a given reference key and callback key.
     * @param {string} refKey - The reference key associated with the callbacks.
     * @param {string} key - The key of the specific callback to notify.
     * @param {boolean} [initial=false] - Whether this is the initial notification.
     */
    _notifyChanges: function (refKey, key, initial) {
      let callbackRecord = Path.get(this._callbacks, `${refKey}.records.${key}`);
      let hasDataFromWorker = Path.get(this._callbacks, `${refKey}.update`) != null;
      if (callbackRecord && hasDataFromWorker) {
        if (callbackRecord.eventType === 'value') {
          let data = Path.get(this._callbacks, `${refKey}.data`);
          // if the data looks like an array make it an array
          if (LikeArray(data)) data = ToArray(data, false);
          let update = initial ? { '/': data } : Path.get(this._callbacks, `${refKey}.update`);
          callbackRecord.callback(this._snapshot({ record: callbackRecord, data, update }));
        } else {
          let children = [];
          if (callbackRecord.eventType === 'child_added') {
            if (initial) {
              children = Path.get(this._callbacks, `${refKey}.data`);
            } else {
              children = Path.get(this._callbacks, `${refKey}.addedChildren`);
            }
          } else if (callbackRecord.eventType === 'child_removed' && !initial)
            children = Path.get(this._callbacks, `${refKey}.removedChildren`);
          else if (callbackRecord.eventType === 'child_changed' && !initial)
            children = Path.get(this._callbacks, `${refKey}.changedChildren`);
          for (let key in children) {
            let snapshot = this._snapshot({ record: callbackRecord, data: children[key], key });
            callbackRecord.callback(snapshot);
          }
        }
      }
    },
    /**
     * Processes an update received from the worker and notifies relevant callbacks.
     * @param {string} refKey - The reference key associated with the update.
     * @param {object} update - The update object containing the changes.
     */
    _processUpdate: function (refKey, update) {
      let callbackRecord = Path.get(this._callbacks, refKey);
      // Check that callback exists.
      // It seems that sometimes when calling off() on a callback there can still be lingering events coming from the worker.
      if (Path.isObject(callbackRecord)) {
        let addedChildren = {};
        let removedChildren = {};
        let changedChildren = {};

        for (let path in update) {
          let pathArray = Path.pathToArray(path, '/');
          if (pathArray.length === 0) {
            // All data is being set.
            if (Path.isObject(update[path])) {
              for (let key in update[path]) {
                let newChildData = update[path][key];
                let prevChildData = Path.get(callbackRecord.data, key);
                // Child has been added.
                if (!Path.valueIsEmpty(newChildData) && Path.valueIsEmpty(prevChildData)) addedChildren[key] = newChildData;
                // Child has been removed.
                else if (Path.valueIsEmpty(newChildData) && !Path.valueIsEmpty(prevChildData)) removedChildren[key] = prevChildData;
                // Child has been set.
                else if (!Path.valueIsEmpty(newChildData)) changedChildren[key] = newChildData;
              }
            } else {
              callbackRecord.data = update[path];
            }
          } else {
            let key = pathArray[0];
            if (pathArray.length === 1) {
              // A child is being set.
              let newChildData = update[path];
              let prevChildData = Path.get(callbackRecord.data, key);
              // Child has been added.
              if (!Path.valueIsEmpty(newChildData) && Path.valueIsEmpty(prevChildData)) addedChildren[key] = newChildData;
              // Child has been removed.
              else if (Path.valueIsEmpty(newChildData) && !Path.valueIsEmpty(prevChildData)) removedChildren[key] = prevChildData;
              // Child has been set.
              else if (!Path.valueIsEmpty(newChildData)) changedChildren[key] = newChildData;
            } else {
              // A child is being modified.
              let prevChildData = Path.get(callbackRecord.data, key);
              let newChildData = changedChildren[key] !== undefined ? changedChildren[key] : Path.copy(prevChildData);
              newChildData = Path.setAndDelete(newChildData, pathArray.slice(1), update[path], '/').base;
              if (Path.valueIsEmpty(newChildData)) {
                // We will not be notifying child changed if newChildData is new.
                delete changedChildren[key];
                // If there was data for this child, we will now notify child removed.
                if (!Path.valueIsEmpty(prevChildData)) {
                  removedChildren[key] = newChildData;
                }
              } else {
                changedChildren[key] = newChildData;
              }
            }
          }
        }

        callbackRecord.update = update;

        callbackRecord.addedChildren = addedChildren;
        callbackRecord.removedChildren = removedChildren;
        callbackRecord.changedChildren = changedChildren;

        for (let key in addedChildren) callbackRecord.data = Path.set(callbackRecord.data, key, addedChildren[key]).base;
        for (let key in removedChildren) callbackRecord.data = Path.delete(callbackRecord.data, key).base;
        for (let key in changedChildren) callbackRecord.data = Path.set(callbackRecord.data, key, changedChildren[key]).base;

        // Notify changes on each callback.
        let callbackRecords = Path.get(this._callbacks, `${refKey}.records`);
        for (let key in callbackRecords) {
          this._notifyChanges(refKey, key);
        }
      }
    },
    _query: function (_record) {
      let boundRef = this._ref.bind(this);
      return {
        get ref() {
          delete this.ref;
          return (this.ref = boundRef(_record));
        },
        dataAsArray: () => {
          console.warn('DATA-AS-ARRAY IS DEPRICATED', _record);
          let _newRecord = Path.copy(_record) || {};
          return this._query(_newRecord);
        },
        endAt: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.endAt = x;
          return this._query(_newRecord);
        },
        equalTo: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.equalTo = x;
          return this._query(_newRecord);
        },
        isEqual: (ref) => {
          // IMPLEMENT.
        },
        limitToFirst: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.limitToFirst = x;
          return this._query(_newRecord);
        },
        limitToLast: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.limitToLast = x;
          return this._query(_newRecord);
        },
        off: (eventType, callback) => {
          if (eventType && callback) {
            // Store event type on record.
            _record.eventType = eventType;
            let refKey = this._getRefKey(_record);
            // Get all callback records for this refKey.
            let callbackRecords = Path.get(this._callbacks, `${refKey}.records`);
            // Search through callback records for one whose callback matches the passed callback.
            for (let key in callbackRecords) {
              let callbackRecord = callbackRecords[key];
              if (callbackRecord.callback === callback) {
                // Delete callback record.
                Path.delete(this._callbacks, `${refKey}.records.${key}`);
                // If not callbacks are left for this refKey, send 'off' event to worker.
                if (Path.get(this._callbacks, `${refKey}.records`) == null) {
                  this._postMessage({ type: 'off', record: this.callbackRecordToRecord(callbackRecord) });
                  Path.delete(this._callbacks, refKey);
                }
                return true;
              }
            }
          } else {
            console.warn('eventType and callback are required');
          }
          return false;
        },
        on: (eventType, callback) => {
          if (eventType && ['value', 'child_added', 'child_changed', 'child_removed'].includes(eventType)) {
            if (callback) {
              // Store event type on record.
              _record.eventType = eventType;
              // Get ref key for this record.
              let refKey = this._getRefKey(_record);
              let callbackRecords = Path.get(this._callbacks, `${refKey}.records`);
              let setupListener = callbackRecords == null;
              let id = firebase.database().ref().push().key;
              // Check that callback isn't already attached.
              if (Object.values(callbackRecords || {}).every((callbackRecord) => callbackRecord.callback !== callback)) {
                // Create callback record.
                Path.set(this._callbacks, `${refKey}.records.${id}`, this.recordToCallbackRecord(_record, callback));
                // If no callbacks currently exist for this refKey, send 'on' event to worker.
                if (setupListener) {
                  this._postMessage({ type: 'on', record: Path.copy(_record) });
                } else {
                  this._notifyChanges(refKey, id, true);
                }
              } else {
                console.warn(`A callback is already attached for ${refKey}`);
              }
            } else {
              console.warn('Callback is required');
            }
          } else {
            console.warn('Invalid eventType');
          }
          return callback;
        },
        once: (eventType, onComplete) => {
          if (eventType && ['value'].includes(eventType)) {
            // Store event type on record.
            _record.eventType = eventType;
            let id = firebase.database().ref().push().key;
            let promise = this._promise(id, _record);
            this._postMessage({ type: 'once', record: Path.copy(_record), id });
            if (onComplete) promise.then((s) => onComplete(s)).catch((e) => onComplete(e));
            return promise;
          } else {
            console.warn('Invalid eventType');
          }
        },
        orderByChild: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.orderBy = x;
          return this._query(_newRecord);
        },
        orderByKey: () => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.orderBy = '$key';
          return this._query(_newRecord);
        },
        orderByPriority: () => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.orderBy = '$priority';
          return this._query(_newRecord);
        },
        orderByValue: () => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.orderBy = '$val';
          return this._query(_newRecord);
        },
        startAt: (x) => {
          let _newRecord = Path.copy(_record) || {};
          _newRecord.queryParams.startAt = x;
          return this._query(_newRecord);
        },
        toJSON: () => {
          // IMPLEMENT.
        },
        toString: () => {
          // IMPLEMENT.
        }
      };
    },
    _ref: function (_record) {
      if (!_record) throw new Error('Cannot create FirebaseWorker Reference without _record');
      // if (_record.path == null) throw new Error('Cannot create FirebaseWorker Reference without _record.path');

      // Initialize/update record properties.
      if (!_record.id) _record.id = firebase.database().ref().push().key;
      if (!_record.queryParams) _record.queryParams = {};
      if (!_record.callbacks) _record.callbacks = {};
      if (!_record.databaseURL) _record.databaseURL = config.firebaseConfigs[config.appName].databaseURL;
      if (!_record.database) _record.database = _record.databaseURL.replace(/(https:\/\/)|(\.firebaseio\.com)/g, '');
      if (!_record.app) _record.app = config.firebaseConfigs[config.appName].projectId;
      if (_record.cache == null) _record.cache = false;
      _record.dbPath = Path.join([_record.database, _record.path], '/');

      let boundApp = this.app.bind(this);
      let temp = Object.assign(this._query(_record), {
        child: (subPath) => {
          subPath = Path.trim(subPath, '/');
          let fullPath = Path.join([_record.path, subPath], '/');
          let _newRecord = Path.copy(_record) || {};
          _newRecord.path = fullPath;
          return this._ref(_newRecord);
        },
        getCachedData: (onComplete) => {
          let id = firebase.database().ref().push().key;
          this._postMessage({ type: 'getCachedData', record: _record, id });
          let promise = this._promise(id, _record);
          if (onComplete) promise.then((data) => onComplete(data)).catch((e) => onComplete(e));
          return promise;
        },
        onDisconnect: () => {
          // IMPLEMENT.
        },
        path: _record.path,
        push: (data, onComplete) => {
          let key = firebase.database().ref().push().key;
          let promise = Promise.resolve();
          if (data) promise = this._ref(_record).child(key).set(data, onComplete);
          promise.key = key;
          return promise;
        },
        record: _record,
        remove: (callback) => {
          return this._ref(_record).set(null, callback);
        },
        set: (data, onComplete, options = {}) => {
          if (data === undefined) throw new Error('Reference.set failed: Was called with 0 arguments. Expects at least 1.');
          // if the set is an empty object, set data to null
          else if (Path.valueIsEmpty(data)) data = null;

          let id = firebase.database().ref().push().key;
          this._postMessage({
            id,
            type: 'set',
            record: _record,
            data,
            options
          });
          let promise = this._promise(id, _record);
          if (onComplete) promise.then((s) => onComplete(null, s)).catch((e) => onComplete(e));

          // Parse events and updates.
          promise._updateParsersPromise = promise.then(() => this.runUpdateParsers(_record.path, data));

          return promise;
        },
        setPriority: () => {
          // IMPLEMENT.
        },
        setWithPriority: () => {
          // IMPLEMENT.
        },
        setCachedData: (data, onComplete) => {
          let id = firebase.database().ref().push().key;
          this._postMessage({ type: 'setCachedData', record: _record, id, data });
          let promise = this._promise(id, _record);
          if (onComplete) promise.then((data) => onComplete(data)).catch((e) => onComplete(e));
          return promise;
        },
        shallow: async (onComplete) => {
          let id = firebase.database().ref().push().key;
          this._postMessage({ type: 'shallow', record: _record, id });
          let promise = this._promise(id, _record);
          if (onComplete) promise.then((data) => onComplete(data)).catch((e) => onComplete(e));
          return promise;
        },
        store: (jobId, options, onComplete) => {
          options = options || {};
          if (location.pathname.includes('mobile')) {
            if (jobId) {
              // Attach onStart callback.
              if (options.onStart) _record.callbacks.onStart = options.onStart;
              // Attach onProgress callback.
              if (options.onProgress) _record.callbacks.onProgress = options.onProgress;
              let id = firebase.database().ref().push().key;
              this._postMessage({ type: 'store', jobId, record: _record, id, force: options.force });
              let promise = this._promise(id, _record);
              if (onComplete) promise.then(() => onComplete(null)).catch((e) => onComplete(e));
              return promise;
            } else console.warn('store: Needs jobId');
          } else console.warn('store: Not using mobile');
        },
        toString: () => {
          return firebase.database().ref(_record.path).toString();
        },
        transaction: () => {
          // IMPLEMENT.
        },
        update: (update, onComplete, options = {}) => {
          if (update === undefined) throw new Error('Reference.update failed: Was called with 0 arguments. Expects at least 1.');

          BlockLargeUpdates(update);

          let id = firebase.database().ref().push().key;
          this._postMessage({
            id,
            type: 'update',
            record: _record,
            update,
            options
          });
          let promise = this._promise(id, _record);
          if (onComplete) promise.then((s) => onComplete(null, s)).catch((e) => onComplete(e));

          // Parse events and updates.
          promise._updateParsersPromise = promise.then(() => this.runUpdateParsers(_record.path, update));

          return promise;
        }
      });
      Object.defineProperty(temp, 'key', {
        get: () => {
          delete this.key;
          let key = _record.path.split('/').slice(-1)[0];
          if (key === '') key = null;
          return (this.key = key);
        }
      });
      Object.defineProperty(temp, 'parent', {
        get: () => {
          delete this.parent;
          if (_record.path)
            return (this.parent = boundApp(_record.app, _record)
              .database(_record.databaseURL)
              .ref(_record.path.split('/').slice(0, -1).join('/')));
          else return (this.parent = null);
        }
      });
      Object.defineProperty(temp, 'root', {
        get: () => {
          delete this.root;
          return (this.root = boundApp(_record.app).database().ref());
        }
      });

      // Part of a workaround for desktop
      if (!self.isMobile) {
        let app = _record.app;
        if (app === config.firebaseConfigs[config.appName].projectId) app = '[DEFAULT]';
        let databaseURL = _record.databaseURL;
        if (databaseURL === config.firebaseConfigs[config.appName].databaseURL) databaseURL = '[DEFAULT]';

        // Get a firebase api reference to return.
        let ref = firebase.app(app).database(databaseURL).ref(_record.path);
        // Replace firebase ref's child method with firebaseWorker's child method.
        ref.child = temp.child;

        // Replace firebase ref's set and update methods with ones that also perform action tracking.
        ref._update = ref.update;
        ref.update = function (update) {
          if (!_record.noTracking) this._actionTracking(_record.path, update);

          BlockLargeUpdates(update);

          let updateIsDonePromise = ref._update(...Array.from(arguments));

          // Parse events and updates.
          updateIsDonePromise._updateParsersPromise = updateIsDonePromise.then(() => this.runUpdateParsers(_record.path, update));

          return updateIsDonePromise;
        }.bind(this);

        ref._set = ref.set;
        ref.set = function (val) {
          if (!_record.noTracking) this._actionTracking(_record.path, val);
          let setIsDonePromise = ref._set(...Array.from(arguments));

          //Parse events and updates.
          setIsDonePromise._updateParsersPromise = setIsDonePromise.then(() => this.runUpdateParsers(_record.path, val));

          return setIsDonePromise;
        }.bind(this);

        return ref;
      }

      return temp;
    },
    _postMessage: function (message) {
      if (this._worker) {
        // Create a shallow copy of message and remove record.callbacks (cannot clone functions).
        let temp = Path.shallowCopy(message);
        temp.record = Path.shallowCopy(temp.record, { callbacks: undefined });
        this._worker.postMessage(temp);
      } else {
        throw new Error(`Attempted to post message to worker before worker was initialized. message: ${JSON.stringify(message)}`);
      }
    },
    _getRefKey: function (record, options) {
      options = options || {};
      let temp = record.dbPath;
      if (!options.excludeQueryParams) {
        for (let key in record.queryParams) {
          temp += `, ${key}:${record.queryParams[key]}`;
        }
      }
      return temp;
    },
    /**
     * Creates and returns a promise that is tracked by FirebaseWorker.
     * @param {string} key - The key to use to track the promise.
     * @param {object} record - The FirebaseWorker record associated with the promise.
     */
    _promise: function (key, record) {
      // Create a promise, exposing its resolve and reject functions in the _promises object.
      let promise = new Promise((resolve, reject) => (this._promises[key] = { resolve, reject, record }));
      // Each promise should clean itself up from the _promises object when it is settled.
      promise.finally(() => delete this._promises[key]);
      // Return the promise.
      return promise;
    },
    /**
     * Creates and returns a Firebase snapshot-like object.
     * @param {object} options - Options for creating the snapshot.
     * @param {any} [options.data] - The data for the snapshot.
     * @param {object} [options.record] - The FirebaseWorker record associated with the snapshot.
     * @param {string} [options.key]
     * @param {object} [options.update]
     */
    _snapshot: function (options) {
      options = options || {};
      if (options.data === undefined) options.data = null;
      let boundRef = this._ref.bind(this);
      let data = Path.isObject(options.data) ? Path.copy(options.data) : options.data;
      let snapshot = {
        get key() {
          delete this.key;
          return (this.key = options.key || snapshot.ref.key);
        },
        get ref() {
          delete this.ref;
          return (this.ref = boundRef(options.record));
        },
        update: function () {
          return options.update;
        },
        child: function (path) {
          // TODO (1/15/2025): This error probably means that this child method won't work.
          let data = Path.get(data, path, '/');
          // TODO (1/15/2025): Include new diff here?
          let record = Path.copy(options.record);
          record.path = Path.join(options.record.path, path, '/');
          return this._snapshot({ data, record });
        },
        exists: function () {
          return data != null;
        },
        exportVal: function () {
          // IMPLEMENT.
        },
        forEach: function () {
          // IMPLEMENT.
        },
        getPriority: function () {
          // IMPLEMENT.
        },
        hasChild: function (path) {
          return Path.get(data, path, '/') != null;
        },
        hasChildren: function () {
          return Path.isObject(data) && Object.values(data).some((x) => x != null);
        },
        numChildren: function () {
          return Path.isObject(data) ? Object.keys(data).length : 0;
        },
        toJSON: function () {
          // IMPLEMENT.
        },
        val: function () {
          return data;
        }
      };
      return snapshot;
    },
    recordToCallbackRecord: function (record, callback) {
      return Object.assign({ callback }, Path.copy(record));
    },
    callbackRecordToRecord: function (callbackRecord) {
      let record = {};
      for (let key in callbackRecord) {
        if (key !== 'callback') {
          Path.set(record, key, Path.copy(callbackRecord[key]));
        }
      }
      return record;
    }
  };
}

var path = self.location.pathname.split('/').filter((x) => x);
if (path.slice(-1)[0].includes('.html')) path.pop();
var resourcesLocation = path.indexOf('_resources');
if (resourcesLocation != -1) {
  path = path.slice(0, resourcesLocation);
  FirebaseWorker._path = self.location.origin + '/' + path.join('/') + '/_resources/js/firebaseWorker';
} else {
  FirebaseWorker._path = self.location.origin + '/' + Path.resolve(path.join('/'), '../_resources/js/firebaseWorker', '/');
}
